(() => {
  const match = (rows, measured, rounding, confirmed = true) => {
    if (!confirmed || !rows.length || !Number.isFinite(measured) || measured <= 0) return null;
    if (!['ceil', 'none'].includes(rounding)) return null;
    if (rows.some(r => !Number.isFinite(r.min) || !Number.isFinite(r.max) || r.min <= 0 || r.min > r.max || !r.option)) return null;
    if (rows.every(r => /^\d+(?:-\d+)?$/.test(r.option))) {
      const labeled = rows.map(r => { const n = r.option.split('-').map(Number); return { row: r, low: n[0], high: n[n.length - 1], paired: n.length === 2 }; });
      if (labeled.some(r => r.low > r.high)) return null;
      const low = Math.min(...labeled.map(r => r.paired ? r.low : r.row.min));
      const high = Math.max(...labeled.map(r => r.high));
      if (measured < low || measured >= high) return null;
      const next = Math.min(...labeled.filter(r => r.high > measured).map(r => r.high));
      const matches = labeled.filter(r => r.high === next);
      return matches.length === 1 ? matches[0].row : null;
    }
    const low = Math.min(...rows.map(r => r.min)), high = Math.max(...rows.map(r => r.max));
    if (measured < low || measured > high) return null;
    if (rounding === 'none' && rows.every(r => r.min === r.max)) {
      const next = Math.min(...rows.filter(r => r.min >= measured).map(r => r.min));
      const matches = rows.filter(r => r.min === next);
      return matches.length === 1 ? matches[0] : null;
    }
    const value = rounding === 'ceil' ? Math.ceil(measured) : measured;
    const matches = rows.filter(r => value >= r.min && value <= r.max);
    return matches.length === 1 ? matches[0] : null;
  };
  const combination = (variants, selected, index, size) => {
    if (index < 0 || selected.some((v, i) => i !== index && !v)) return null;
    const matches = variants.filter(v => v.options[index] === size && v.options.every((x, i) => i === index || x === selected[i]));
    return matches.length === 1 && matches[0].available ? matches[0] : null;
  };
  const finite = value => typeof value === 'number' && Number.isFinite(value);
  const positive = value => finite(value) && value > 0;
  const nonnegative = value => finite(value) && value >= 0;
  const modes = { pin: 'hole_geometry', ratchet: 'track_range_geometry', stretch_woven: 'measured_comfort_range', continuous_clamp: 'continuous_tail_geometry' };
  const geometryProfile = (data, options) => {
    if (!data || typeof data !== 'object' || data.version !== 1 || data.unit !== 'mm' || data.input_basis !== 'waist_over_trousers') return null;
    if (!Object.hasOwn(modes, data.type) || data.fit_mode !== modes[data.type]) return null;
    if (data.status !== 'confirmed' && !(data.type === 'stretch_woven' && data.status === 'owner_confirmed_range')) return null;
    if (!Array.isArray(options) || !options.length || options.some(v => typeof v !== 'string' || !v.trim()) || new Set(options).size !== options.length) return null;
    if (!Array.isArray(data.sizes) || data.sizes.length !== options.length || new Set(data.sizes.map(s => s?.size_value)).size !== options.length) return null;
    if (data.sizes.some(s => !s || !options.includes(s.size_value))) return null;
    const c = data.common;
    if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
    if (data.selection_policy && (typeof data.selection_policy !== 'object' || (data.selection_policy.rounding && !['none', 'ceil_whole_inch'].includes(data.selection_policy.rounding)))) return null;
    if (data.type !== 'stretch_woven' && !finite(c.closure_correction_mm)) return null;
    if (data.type === 'pin' && (!Number.isSafeInteger(c.hole_count) || c.hole_count < 1 || !positive(c.hole_pitch_mm) || !nonnegative(c.last_hole_to_tip_mm))) return null;
    if (data.type === 'ratchet' && (!positive(c.track_length_mm) || !nonnegative(c.tail_length_mm))) return null;
    if (data.type === 'continuous_clamp' && (!nonnegative(c.min_safe_tail_mm) || !nonnegative(c.max_accepted_tail_mm) || c.min_safe_tail_mm > c.max_accepted_tail_mm)) return null;
    const rows = data.sizes.map(s => {
      let low, high;
      if (data.type === 'pin') {
        if (!positive(s.first_hole_from_fold_mm)) return null;
        low = s.first_hole_from_fold_mm + c.closure_correction_mm;
        high = low + (c.hole_count - 1) * c.hole_pitch_mm;
      } else if (data.type === 'ratchet') {
        if (!positive(s.assembled_strap_length_mm) || s.assembled_strap_length_mm <= c.tail_length_mm + c.track_length_mm) return null;
        high = s.assembled_strap_length_mm - c.tail_length_mm + c.closure_correction_mm;
        low = high - c.track_length_mm;
      } else if (data.type === 'continuous_clamp') {
        if (!positive(s.assembled_strap_length_mm) || s.assembled_strap_length_mm <= c.max_accepted_tail_mm) return null;
        low = s.assembled_strap_length_mm - c.max_accepted_tail_mm + c.closure_correction_mm;
        high = s.assembled_strap_length_mm - c.min_safe_tail_mm + c.closure_correction_mm;
      } else {
        if (!positive(s.fit_min_mm) || !positive(s.fit_max_mm)) return null;
        low = s.fit_min_mm; high = s.fit_max_mm;
      }
      if (!positive(low) || !positive(high) || low > high) return null;
      return { option: s.size_value, min: low, max: high, center: low + (high - low) / 2, data: s };
    });
    if (rows.some(r => !r)) return null;
    return { type: data.type, common: c, rows, options: [...options], policy: data.selection_policy };
  };
  const dimensionProfile = (data, options) => {
    const profile = geometryProfile(data?.status === 'pending' ? { ...data, status: 'confirmed' } : data, options);
    return profile ? { ...profile, pending: data.status === 'pending' } : null;
  };
  const preciseMatch = (profile, measured, preferredHole = 0) => {
    if (!profile || !positive(measured)) return null;
    const epsilon = 1e-7;
    let target = measured * 25.4;
    if (!positive(target)) return null;
    if (profile.type === 'stretch_woven' && profile.policy) {
      const p = profile.policy;
      if (p.rounding && !['none', 'ceil_whole_inch'].includes(p.rounding)) return null;
      if (target < Math.min(...profile.rows.map(r => r.min)) - epsilon || target > Math.max(...profile.rows.map(r => r.max)) + epsilon) return null;
      if (p.rounding === 'ceil_whole_inch') target = Math.ceil(measured) * 25.4;
    }
    if (profile.type === 'pin' && preferredHole !== 0) {
      if (!Number.isSafeInteger(preferredHole) || preferredHole < 1 || preferredHole > profile.common.hole_count) return null;
      if (target < Math.min(...profile.rows.map(row => row.min)) - epsilon || target > Math.max(...profile.rows.map(row => row.max)) + epsilon) return null;
      const selected = profile.rows.map(row => {
        const fitted = row.min + (preferredHole - 1) * profile.common.hole_pitch_mm;
        return { ...row, hole: preferredHole, fitted_mm: fitted, difference_mm: fitted - target,
          tail_mm: profile.common.last_hole_to_tip_mm + (profile.common.hole_count - preferredHole) * profile.common.hole_pitch_mm,
          slack: Math.max(0, fitted - target), distance: Math.abs(fitted - target) };
      }).filter(row => row.difference_mm >= -25.4 - epsilon && row.difference_mm <= 50.8 + epsilon);
      selected.sort((a, b) => Math.abs(a.distance - b.distance) > epsilon ? a.distance - b.distance : b.difference_mm - a.difference_mm);
      if (selected.length > 1 && Math.abs(selected[0].distance - selected[1].distance) < epsilon && Math.abs(selected[0].difference_mm - selected[1].difference_mm) < epsilon) return null;
      return selected[0] || null;
    }
    const candidates = profile.rows.filter(r => target >= r.min - epsilon && target <= r.max + epsilon).map(row => {
      let fitted = target, hole = null, tail = null;
      let score = Math.abs(target - row.center) / Math.max(1, row.max - row.min);
      if (profile.type === 'pin') {
        hole = Math.min(profile.common.hole_count, Math.max(1, Math.ceil((target - row.min) / profile.common.hole_pitch_mm - epsilon) + 1));
        fitted = row.min + (hole - 1) * profile.common.hole_pitch_mm;
        tail = profile.common.last_hole_to_tip_mm + (profile.common.hole_count - hole) * profile.common.hole_pitch_mm;
        score = Math.abs(hole - (profile.common.hole_count + 1) / 2) / profile.common.hole_count;
      } else if (profile.type !== 'stretch_woven') {
        tail = row.data.assembled_strap_length_mm + profile.common.closure_correction_mm - target;
      }
      return { ...row, hole, tail_mm: tail, fitted_mm: fitted, slack: Math.max(0, fitted - target), score };
    });
    if (!candidates.length) return null;
    if (profile.type === 'stretch_woven') {
      candidates.sort((a, b) => b.center - a.center);
    } else {
      candidates.sort((a, b) => a.slack - b.slack || a.score - b.score || b.center - a.center);
    }
    if (candidates.length > 1 && Math.abs(candidates[0].center - candidates[1].center) < epsilon && Math.abs(candidates[0].slack - candidates[1].slack) < epsilon && Math.abs(candidates[0].score - candidates[1].score) < epsilon) return null;
    return candidates[0];
  };
  const pinHoleFits = (profile, selected, targetIn) => {
    if (profile?.type !== 'pin' || !selected || !positive(targetIn)) return null;
    const target = targetIn * 25.4, epsilon = 1e-7;
    const closest = row => {
      const hole = Math.min(profile.common.hole_count, Math.max(1, 1 + Math.floor((target - row.min) / profile.common.hole_pitch_mm + 0.5 + epsilon)));
      const fitted = row.min + (hole - 1) * profile.common.hole_pitch_mm;
      return { option: row.option, hole, fitted_mm: fitted, difference_mm: fitted - target };
    };
    const rows = [...profile.rows].sort((a, b) => a.center - b.center);
    const index = rows.findIndex(row => row.option === selected.option);
    if (index < 0) return null;
    const nearby = [rows[index - 1], rows[index + 1]].filter(Boolean).map(closest)
      .filter(row => row.difference_mm >= -25.4 - epsilon && row.difference_mm <= 50.8 + epsilon)
      .sort((a, b) => Math.abs(a.difference_mm) - Math.abs(b.difference_mm) || b.fitted_mm - a.fitted_mm);
    return { recommended: closest(rows[index]), nearby };
  };
  const resolve = ({ geometry, options, rows, measured, rounding, confirmed = true, guidanceOnly = false, preferredHole = 0, comfortAllowanceIn = 0, measurementMode = 'waist' }) => {
    if (guidanceOnly) return { mode: 'guidance', row: null, profile: null };
    const profile = geometryProfile(geometry, options);
    const mode = profile ? 'precision' : 'legacy';
    if (!positive(measured) || !['waist', 'belt'].includes(measurementMode) || ![0, 1, 2].includes(comfortAllowanceIn)) return { mode, row: null, profile };
    const allowance = measurementMode === 'waist' ? comfortAllowanceIn : 0;
    const target = measured + allowance;
    if (profile) return { mode: 'precision', row: preciseMatch(profile, target, preferredHole), profile, target, allowance };
    return { mode: 'legacy', row: match(rows, target, rounding, confirmed), profile: null, target, allowance };
  };
  window.FVSizeGuide = { match, combination, geometryProfile, dimensionProfile, preciseMatch, pinHoleFits, resolve };
  if (customElements.get('fv-size-guide')) return;
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-fv-size-link]');
    const opener = link?.closest('side-drawer-opener');
    const selector = opener?.getAttribute('data-side-drawer');
    const drawer = selector ? document.querySelector(selector) : null;
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(link);
  });
  class Guide extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.panels = [...this.querySelectorAll('[data-product]')];
      this.input = this.querySelector('[data-waist]');
      this.status = this.querySelector('[data-status]');
      this.result = this.querySelector('[data-result]');
      if (!this.input || !this.status || !this.result) return;
      this.classList.add('enhanced');
      const entry = new URLSearchParams(location.search);
      this.origin = entry.get('from') === 'product' ? entry.get('product') : null;
      this.originVariant = /^\d+$/.test(entry.get('variant') || '') ? entry.get('variant') : null;
      this.select(this.dataset.mode === 'native' ? this.panels[0]?.dataset.product : entry.get('product'));
      const picker = this.querySelector('.product-picker');
      if (picker && this.panel) picker.open = false;
      this.addEventListener('input', event => {
        if (event.target === this.input) this.recommend();
        if (event.target.matches('[data-search]')) {
          const term = event.target.value.trim().toLowerCase();
          const cards = [...this.querySelectorAll('[data-choose]')];
          cards.forEach(c => { c.hidden = !c.dataset.searchTerm.toLowerCase().includes(term); });
          this.querySelector('[data-search-empty]').hidden = cards.some(c => !c.hidden);
        }
      });
      this.addEventListener('click', event => {
        const card = event.target.closest('[data-choose]');
        if (card) { event.preventDefault(); this.select(card.dataset.choose); this.querySelector('.product-picker').open = false; if (this.panel.dataset.guidanceOnly === 'true') this.panel.querySelector('h3')?.focus({ preventScroll: true }); else this.input.focus(); }
        const unit = event.target.closest('[data-unit]');
        if (unit) { this.classList.toggle('cm-mode', unit.dataset.unit === 'cm'); this.querySelectorAll('[data-unit]').forEach(b => b.setAttribute('aria-pressed', String(b === unit))); }
        if (event.target.closest('[data-purchase]') && this.dataset.mode === 'native') { event.preventDefault(); this.useSize(); }
      });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-preferred-hole], [data-fit-preference]')) { this.recommend(); return; }
        if (!event.target.matches('[data-measurement]')) return;
        this.input.value = '';
        this.updateMeasurement();
        this.recommend();
      });
      this.updateMeasurement();
      const info = this.owner();
      if (info) {
        info.addEventListener('change', event => { if (event.target.closest('variant-selects')) this.recommend(); });
        this.restoreQuery();
      }
    }
    select(handle) {
      if (this.panel?.dataset.product !== handle) {
        const preference = this.querySelector('[data-fit-preference]');
        if (preference) preference.value = '1';
        const advanced = this.querySelector('[data-fit-advanced]');
        if (advanced) advanced.open = false;
      }
      this.panel = this.panels.find(p => p.dataset.product === handle);
      this.panels.forEach(p => { p.hidden = p !== this.panel; });
      this.querySelectorAll('[data-choose]').forEach(c => c.setAttribute('aria-current', c.dataset.choose === handle ? 'true' : 'false'));
      const title = this.querySelector('[data-selected-title]');
      if (title) title.textContent = this.panel?.querySelector('h2').textContent || '';
      this.querySelectorAll('[data-selected-product]').forEach(item => { item.hidden = item.dataset.selectedProduct !== this.panel?.dataset.product; });
      const prompt = this.querySelector('[data-prompt]');
      if (prompt) prompt.hidden = Boolean(this.panel);
      this.recommend();
    }
    rows() { return [...(this.panel?.querySelectorAll('tr[data-option]') || [])].map(el => ({ min: el.dataset.low?.trim() ? Number(el.dataset.low) : NaN, max: el.dataset.high?.trim() ? Number(el.dataset.high) : NaN, option: el.dataset.option, el })); }
    updateMeasurement() {
      const mode = this.querySelector('[data-measurement]')?.value || 'waist';
      const key = { waist: 'textWaist', belt: 'textBelt' }[mode] || 'textWaist';
      const label = this.querySelector('[data-waist-label]'), hint = this.querySelector('[data-input-hint]');
      if (label) label.textContent = label.dataset[key] || label.dataset.textWaist;
      if (hint) hint.textContent = hint.dataset[key] || hint.dataset.textWaist;
    }
    geometry() {
      const element = this.panel?.querySelector('[data-fv-geometry]');
      try { return element ? { geometry: JSON.parse(element.dataset.geometry), size_options: JSON.parse(element.dataset.sizeOptions) } : {}; } catch { return {}; }
    }
    updateHolePicker(profile) {
      const field = this.querySelector('[data-hole-preference]');
      if (!field) return;
      const enabled = profile?.type === 'pin' && profile.common.hole_count <= 50;
      field.hidden = !enabled;
      if (!enabled) { this.holePickerKey = null; field.querySelector('[data-hole-options]').replaceChildren(); return; }
      const key = this.panel.dataset.product + ':' + profile.common.hole_count;
      if (this.holePickerKey === key) return;
      this.holePickerKey = key;
      const options = field.querySelector('[data-hole-options]');
      options.replaceChildren();
      const middle = Math.ceil(profile.common.hole_count / 2);
      for (let hole = 1; hole <= profile.common.hole_count; hole++) {
        const label = document.createElement('label');
        label.className = 'hole-option';
        const input = document.createElement('input');
        input.type = 'radio'; input.name = this.input.id + '-hole'; input.value = String(hole);
        input.dataset.preferredHole = ''; input.checked = hole === middle;
        input.setAttribute('aria-label', (hole === middle ? field.dataset.middleLabel : field.dataset.holeLabel).replace('{hole}', String(hole)));
        const mark = document.createElement('span'); mark.className = 'hole-marker'; mark.textContent = String(hole);
        label.append(input, mark); options.append(label);
      }
    }
    updateFitSettings(profile) {
      const advanced = this.querySelector('[data-fit-advanced]');
      if (!advanced) return;
      const waist = (this.querySelector('[data-measurement]')?.value || 'waist') === 'waist';
      const preference = this.querySelector('[data-fit-preference]');
      const fit = this.querySelector('[data-fit-field]');
      if (fit) fit.hidden = !waist;
      if (preference) preference.disabled = !waist;
      advanced.hidden = this.input.disabled || (!waist && profile?.type !== 'pin');
      const summary = this.querySelector('[data-fit-summary]');
      const hole = this.querySelector('[data-preferred-hole]:checked');
      const field = this.querySelector('[data-hole-preference]');
      if (summary) summary.textContent = [waist ? preference?.selectedOptions[0]?.textContent : null, hole && field && !field.hidden ? hole.getAttribute('aria-label') : null].filter(Boolean).join(' · ');
    }
    copy() { return { ...this.panel?.querySelector('[data-precision-copy]')?.dataset, ...this.panel?.querySelector('[data-spec-copy]')?.dataset }; }
    precisionChart(profile) {
      const container = this.panel?.querySelector('[data-precision-chart]');
      if (!container) return;
      const legacy = this.panel.querySelector('[data-legacy-chart]');
      container.hidden = !profile;
      if (legacy) legacy.hidden = Boolean(profile);
      this.panel.classList.toggle('product-panel--precision', Boolean(profile));
      if (!profile || container.dataset.rendered === profile.type + ':' + profile.pending) return;
      container.replaceChildren();
      const copy = this.copy();
      const heading = document.createElement('h3'); heading.className = 'chart-heading'; heading.textContent = copy.caption;
      const scroll = document.createElement('div'); scroll.className = 'chart-scroll'; scroll.tabIndex = 0;
      scroll.setAttribute('role', 'region'); scroll.setAttribute('aria-label', copy.scrollLabel);
      const table = document.createElement('table'); table.className = 'spec-table spec-table--unified'; table.setAttribute('aria-label', copy.caption);
      const woven = profile.type === 'stretch_woven';
      table.classList.toggle('spec-table--woven', woven);
      const head = table.createTHead().insertRow();
      const headers = [copy.beltSize, profile.type === 'pin' ? copy.holeLengths : woven ? copy.naturalLength : copy.length];
      for (const text of headers) {
        const th = document.createElement('th'); th.scope = 'col'; th.textContent = text; head.append(th);
      }
      const dimension = (cell, low, high = low) => {
        if (!nonnegative(low) || !nonnegative(high)) {
          const missing = document.createElement('span'); missing.textContent = '—'; missing.setAttribute('aria-label', copy.unknown); cell.append(missing); return;
        }
        for (const [unit, divisor] of [['in', 25.4], ['cm', 10]]) {
          const span = document.createElement('span'); span.className = 'unit-' + unit;
          const format = value => unit === 'in' ? (Math.round((value / divisor + Number.EPSILON) * 10) / 10).toFixed(1) : (value / divisor).toFixed(2).replace(/\.?0+$/, '');
          span.textContent = format(low) + (low === high ? '' : '–' + format(high)) + ' ' + unit; cell.append(span);
        }
      };
      const body = table.createTBody();
      for (const row of profile.rows) {
        const tr = body.insertRow(); tr.dataset.geometryOption = row.option;
        const size = document.createElement('th'); size.scope = 'row'; size.textContent = row.option; tr.append(size);
        const c = profile.common, length = tr.insertCell();
        if (profile.type === 'pin') dimension(length, row.data.first_hole_from_fold_mm, row.data.first_hole_from_fold_mm + (c.hole_count - 1) * c.hole_pitch_mm);
        else dimension(length, woven ? row.data.relaxed_strap_length_mm : row.data.assembled_strap_length_mm);
      }
      scroll.append(table);
      const hint = document.createElement('p'); hint.className = 'chart-scroll-hint'; hint.textContent = copy.scrollHint;
      const definitions = document.createElement('p'); definitions.className = 'chart-definitions';
      definitions.textContent = profile.type === 'pin' ? copy.pinDimensionsNote : copy.lengthNote + (woven ? ' ' + copy.wovenTailNote : '');
      const note = document.createElement('p'); note.className = profile.pending ? 'chart-pending-note' : 'hint'; note.textContent = profile.pending ? copy.pendingDimensions : copy.measurementsNote;
      container.append(heading, note, scroll, hint, definitions); container.dataset.rendered = profile.type + ':' + profile.pending;
    }
    recommend() {
      this.panels.forEach(p => p.querySelectorAll('.is-match').forEach(r => r.classList.remove('is-match')));
      this.result.hidden = true; this.recommended = null;
      const targetResult = this.querySelector('[data-fit-target]');
      if (targetResult) { targetResult.hidden = true; targetResult.textContent = ''; }
      const holeResult = this.querySelector('[data-hole-result]');
      if (holeResult) { holeResult.hidden = true; holeResult.textContent = ''; }
      const wearingResult = this.querySelector('[data-wearing-hole]');
      const nearbyResult = this.querySelector('[data-nearby-holes]');
      for (const result of [wearingResult, nearbyResult]) if (result) { result.hidden = true; result.textContent = ''; }
      const guidanceOnly = this.panel?.dataset.guidanceOnly === 'true';
      this.classList.toggle('fv-guide--trim-one', guidanceOnly);
      this.querySelector('.waist-controls').hidden = guidanceOnly;
      this.status.hidden = guidanceOnly;
      const faq = this.querySelector('[data-fit-faq]');
      const question = this.querySelector('[data-fit-question]');
      if (question) question.textContent = guidanceOnly ? question.dataset.trim : question.dataset.general;
      if (guidanceOnly) { this.updateHolePicker(null); const advanced = this.querySelector('[data-fit-advanced]'); if (advanced) advanced.hidden = true; if (faq) faq.textContent = faq.dataset.trim; this.dataset.calculationMode = 'guidance'; this.input.disabled = true; return; }
      const data = this.geometry();
      const profile = geometryProfile(data.geometry, data.size_options);
      this.precisionChart(dimensionProfile(data.geometry, data.size_options));
      this.updateHolePicker(profile);
      this.dataset.calculationMode = profile ? 'precision' : 'legacy';
      if (faq) faq.textContent = profile ? faq.dataset.precision : faq.dataset.legacy;
      this.input.disabled = !this.panel || (!profile && (this.panel.dataset.confirmed !== 'true' || !this.rows().length));
      this.updateFitSettings(profile);
      this.status.textContent = this.dataset.start || 'Choose a belt and enter your measured waist in inches.';
      if (this.input.disabled) { this.status.textContent = this.dataset.help || 'Ask us for fit help. This belt has no confirmed matching size options.'; return; }
      if (!this.input.value.trim()) return;
      const preferredHole = Number(this.querySelector('[data-preferred-hole]:checked')?.value || (profile?.type === 'pin' ? Math.ceil(profile.common.hole_count / 2) : 0));
      const measurementMode = this.querySelector('[data-measurement]')?.value || 'waist';
      const comfortAllowanceIn = Number(this.querySelector('[data-fit-preference]')?.value || 0);
      const calculation = resolve({ measurementMode, comfortAllowanceIn, preferredHole, geometry: data.geometry, options: data.size_options, rows: this.rows(), measured: Number(this.input.value), rounding: this.panel.dataset.rounding, confirmed: this.panel.dataset.confirmed === 'true' });
      if (targetResult && positive(calculation.target)) {
        const template = measurementMode === 'belt' ? targetResult.dataset.templateBelt : targetResult.dataset.templateWaist;
        targetResult.textContent = template.replace('{target}', calculation.target.toFixed(1)).replace('{allowance}', calculation.allowance.toFixed(1));
        targetResult.hidden = false;
      }
      const row = calculation.row;
      if (!row) { this.status.textContent = profile?.type === 'pin' && preferredHole ? wearingResult?.dataset.noMatch || this.dataset.noMatch : this.dataset.noMatch || 'No unique size matches this measurement. Contact us for fit help.'; return; }
      const element = row.el || [...this.panel.querySelectorAll('[data-geometry-option]')].find(el => el.dataset.geometryOption === row.option);
      element?.classList.add('is-match');
      this.panel.querySelectorAll('[data-geometry-option]').forEach(el => el.classList.toggle('is-match', el.dataset.geometryOption === row.option));
      this.recommended = row.option;
      if (this.dataset.mode === 'native' && !this.targetVariant(row.option)) { this.status.textContent = this.dataset.unavailable || 'This size is unavailable with your current options. Your selection has been kept. Contact us for help.'; return; }
      this.querySelector('[data-recommended]').textContent = row.option;
      const link = this.querySelector('[data-purchase]');
      const url = new URL(this.panel.dataset.url, location.origin);
      const same = this.origin === this.panel.dataset.product;
      if (same && this.originVariant) url.searchParams.set('variant', this.originVariant);
      if (same) url.searchParams.set('fv_size', row.option);
      link.href = url.pathname + url.search;
      link.textContent = this.dataset.mode === 'native' || same ? (this.dataset.use || 'Use This Size') : (this.dataset.shop || 'Shop This Belt');
      this.result.hidden = false;
      if (calculation.mode === 'precision') {
        this.status.textContent = this.copy().matched;
        if (preferredHole && profile.type === 'pin' && holeResult) {
          const field = this.querySelector('[data-hole-preference]');
          const difference = (Math.round(Math.abs(row.difference_mm) / 25.4 * 10) / 10).toFixed(1);
          const template = difference === '0.0' ? field.dataset.equal : row.difference_mm > 0 ? field.dataset.larger : field.dataset.smaller;
          holeResult.textContent = template.replace('{hole}', String(row.hole)).replace('{fit}', (Math.round(row.fitted_mm / 25.4 * 10) / 10).toFixed(1)).replace('{difference}', difference);
          holeResult.hidden = false;
          const fits = pinHoleFits(profile, row, calculation.target);
          const formatFit = (fit, template) => template.replace('{size}', fit.option).replace('{hole}', String(fit.hole)).replace('{fit}', (Math.round(fit.fitted_mm / 25.4 * 10) / 10).toFixed(1));
          if (wearingResult && fits) {
            wearingResult.textContent = formatFit(fits.recommended, wearingResult.dataset.template);
            wearingResult.hidden = false;
          }
          if (nearbyResult && fits) {
            const nearby = fits.nearby.filter(fit => this.dataset.mode !== 'native' || this.targetVariant(fit.option));
            nearbyResult.textContent = nearby.map(fit => formatFit(fit, nearbyResult.dataset.template)).join(' · ');
            nearbyResult.hidden = nearby.length === 0;
          }
        }
        return;
      }
      if (/^\d+(?:-\d+)?$/.test(row.option)) { this.status.textContent = this.dataset.matched; return; }
      this.status.textContent = this.panel.dataset.rounding === 'ceil' && Math.ceil(Number(this.input.value)) !== Number(this.input.value) ? (this.dataset.rounded || 'Fractional inches rounded up within the confirmed fit limits.') : (this.dataset.matched || 'Matching size highlighted.');
    }
    owner() { return document.getElementById(this.dataset.owner) || this.closest('product-info'); }
    nativeState() {
      const info = this.owner();
      const picker = info?.querySelector('variant-selects[data-context="main"]') || info?.querySelector('variant-selects');
      const evidence = this.panel?.querySelector('[data-fv-variants]');
      if (!picker || !evidence) return null;
      const data = JSON.parse(evidence.textContent);
      if (data.count !== data.variants.length) return null;
      const groups = [...picker.querySelectorAll('fieldset, select')];
      if (groups.length !== data.options.length) return null;
      const selected = groups.map(g => g.tagName === 'SELECT' ? g.value : g.querySelector('input:checked')?.value);
      return { info, picker, groups, data, selected, index: Number(this.panel.dataset.sizeIndex) };
    }
    targetVariant(size) {
      const state = this.nativeState();
      return state && combination(state.data.variants, state.selected, state.index, size);
    }
    useSize(size = this.recommended) {
      const state = this.nativeState(), target = this.targetVariant(size);
      if (!target || !state) { this.result.hidden = true; this.status.textContent = this.dataset.unavailable || 'This size is unavailable with your current options. Your selection has been kept. Contact us for help.'; return false; }
      const group = state.groups[state.index];
      const input = group.tagName === 'SELECT' ? [...group.options].find(o => o.value === size) : [...group.querySelectorAll('input[type="radio"]')].find(i => i.value === size);
      if (!input || input.disabled || input.classList.contains('disabled') || !input.dataset.optionValueId) return false;
      if (group.tagName === 'SELECT') group.value = size; else input.checked = true;
      const drawer = this.closest('side-drawer');
      const closed = drawer?.close ? drawer.close() : Promise.resolve();
      state.info.dataset.fvSizePending = 'false';
      (group.tagName === 'SELECT' ? group : input).dispatchEvent(new Event('change', { bubbles: true }));
      const focusSize = () => {
        const livePicker = state.info.querySelector('variant-selects[data-context="main"]') || state.info.querySelector('variant-selects');
        const live = [...(livePicker?.querySelectorAll('input[type="radio"], select') || [])].find(i => i.value === size);
        live?.focus({ preventScroll: true });
      };
      Promise.resolve(closed).then(() => requestAnimationFrame(focusSize));
      const observer = new MutationObserver(() => { focusSize(); observer.disconnect(); });
      observer.observe(state.info, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 4000);
      return true;
    }
    restoreQuery() {
      if (this.dataset.mode !== 'native' || window.FVSizeGuide.queryHandled) return;
      const entry = new URLSearchParams(location.search), size = entry.get('fv_size');
      if (!size || this.panel?.dataset.guidanceOnly === 'true') return;
      const state = this.nativeState();
      if (!state) return;
      const original = state.data.variants.find(v => String(v.id) === entry.get('variant'));
      window.FVSizeGuide.queryHandled = true;
      const geometry = this.geometry(), profile = geometryProfile(geometry.geometry, geometry.size_options);
      const validSize = profile ? profile.options.includes(size) : this.rows().some(r => r.option === size);
      if (!validSize || !original || state.selected.some((v, i) => i !== state.index && v !== original.options[i]) || !this.useSize(size)) {
        this.status.textContent = this.dataset.unavailable || 'The requested size cannot be selected with the original options. Your selection has been kept. Contact us for help.';
        this.status.setAttribute('tabindex', '-1');
        const drawer = this.closest('side-drawer');
        if (drawer?.open) drawer.open(document.querySelector('[data-fv-size-link]'));
        this.status.focus();
      }
    }
  }
  customElements.define('fv-size-guide', Guide);
})();
